<template>
    <BaseModal
        v-if="show"
        :title="isEditing ? 'Edit Class' : 'Add Class'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="7xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <!-- Basic Information Section -->
                <div>
                    <h4
                        class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                    >
                        Basic Information
                    </h4>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Class Code *
                            </label>
                            <input
                                v-model="form.code"
                                @input="formatCode"
                                type="text"
                                required
                                placeholder="e.g., X-IPA-1, XI-IPS-2"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                            <p
                                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                Uppercase, no spaces (e.g., X-IPA-1)
                            </p>
                            <p
                                v-if="errors.code"
                                class="mt-1 text-sm text-danger-600"
                            >
                                {{ errors.code }}
                            </p>
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Class Name *
                            </label>
                            <input
                                v-model="form.name"
                                type="text"
                                required
                                placeholder="e.g., X IPA 1, XI IPS 2"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                            <p
                                v-if="errors.name"
                                class="mt-1 text-sm text-danger-600"
                            >
                                {{ errors.name }}
                            </p>
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Grade Level *
                            </label>
                            <select
                                v-model="form.grade_level"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">Select Grade Level</option>
                                <option value="X">X (10th Grade)</option>
                                <option value="XI">XI (11th Grade)</option>
                                <option value="XII">XII (12th Grade)</option>
                            </select>
                            <p
                                v-if="errors.grade_level"
                                class="mt-1 text-sm text-danger-600"
                            >
                                {{ errors.grade_level }}
                            </p>
                        </div>
                        <div>
                            <SearchableSelect
                                v-model="form.major_id"
                                :options="majorStore.activeMajors"
                                label="Major"
                                placeholder="Select Major"
                                optionLabel="name"
                                optionValue="id"
                                :error="errors.major_id"
                            />
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Capacity *
                            </label>
                            <input
                                v-model.number="form.capacity"
                                type="number"
                                min="1"
                                max="100"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                            <p
                                v-if="errors.capacity"
                                class="mt-1 text-sm text-danger-600"
                            >
                                {{ errors.capacity }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Assignment Section -->
                <div>
                    <h4
                        class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                    >
                        Assignment
                    </h4>
                    <div>
                        <SearchableSelect
                            v-model="form.homeroom_teacher_id"
                            :options="homeroomTeachers"
                            label="Homeroom Teacher"
                            placeholder="Select Homeroom Teacher"
                            :optionLabel="
                                (teacher) =>
                                    `${teacher.user?.name || teacher.teacher_id} (${teacher.nip})`
                            "
                            optionValue="id"
                            :error="errors.homeroom_teacher_id"
                        />
                    </div>
                </div>

                <!-- Settings Section -->
                <div>
                    <h4
                        class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                    >
                        Settings
                    </h4>
                    <div class="flex items-center">
                        <input
                            v-model="form.is_active"
                            type="checkbox"
                            id="is_active"
                            class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label
                            for="is_active"
                            class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                        >
                            Active Class
                        </label>
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import type { ClassSchooll } from "@/api/classSchooll";
import { useTeacherStore } from "@/stores/teacherStore";
import { useMajorStore } from "@/stores/majorStore";
import { useClassSchoollStore } from "@/stores/classSchoollStore";
import type { Teacher } from "@/api/teacher";
import BaseModal from "@/components/BaseModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";

const props = defineProps<{
    show: boolean;
    classSchooll?: ClassSchooll | null;
    isEditing: boolean;
}>();

const emit = defineEmits<{
    close: [];
    saved: [];
}>();

const teacherStore = useTeacherStore();
const majorStore = useMajorStore();
const classSchoollStore = useClassSchoollStore();

const loading = ref(false);
const errors = ref<Record<string, string>>({});
const homeroomTeachers = ref<Teacher[]>([]);

const form = ref({
    code: "",
    name: "",
    grade_level: "",
    major_id: null as string | null,
    homeroom_teacher_id: null as string | null,
    capacity: 40,
    is_active: true,
});

const resetForm = () => {
    form.value = {
        code: "",
        name: "",
        grade_level: "",
        major_id: null as string | null,
        homeroom_teacher_id: null as string | null,
        capacity: 40,
        is_active: true,
    };
    errors.value = {};
};

const loadHomeroomTeachers = async () => {
    try {
        const response = await teacherStore.fetchHomeroomTeachers();
        homeroomTeachers.value = response.data || [];
        console.log("Loaded homeroom teachers:", homeroomTeachers.value);
    } catch (error) {
        console.error("Failed to load homeroom teachers:", error);
        homeroomTeachers.value = [];
    }
};

const loadMajors = async () => {
    try {
        await majorStore.fetchActiveMajors();
        console.log("Loaded majors:", majorStore.activeMajors);
    } catch (error) {
        console.error("Failed to load majors:", error);
    }
};

// Initialize form when editing
watch(
    () => props.classSchooll,
    () => {
        if (props.classSchooll) {
            form.value = {
                code: props.classSchooll.code || "",
                name: props.classSchooll.name || "",
                grade_level: props.classSchooll.grade_level || "",
                major_id: props.classSchooll.major_id || null,
                homeroom_teacher_id:
                    props.classSchooll.homeroom_teacher?.id || null,
                capacity: props.classSchooll.capacity || 40,
                is_active: props.classSchooll.is_active ?? true,
            };
            console.log("Form initialized for editing:", form.value);
            console.log(
                "Homeroom teacher ID from class:",
                props.classSchooll.homeroom_teacher?.id,
            );
        } else {
            resetForm();
        }
    },
    { immediate: true },
);

const formatCode = () => {
    // Format code: uppercase and remove spaces
    form.value.code = form.value.code.toUpperCase().replace(/\s+/g, "");
};

const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.value.code.trim()) {
        newErrors.code = "Class code is required";
    }

    // Validate code format: uppercase letters, numbers, and hyphens only
    const codeRegex = /^[A-Z0-9-]+$/;
    if (!codeRegex.test(form.value.code)) {
        newErrors.code =
            "Class code must be uppercase with no spaces (e.g., X-IPA-1, XI-IPS-2).";
    }

    if (!form.value.name.trim()) {
        newErrors.name = "Class name is required";
    }

    if (!form.value.grade_level.trim()) {
        newErrors.grade_level = "Grade level is required";
    }

    if (!form.value.major_id) {
        newErrors.major_id = "Major is required";
    }

    if (form.value.capacity < 1 || form.value.capacity > 100) {
        newErrors.capacity = "Capacity must be between 1 and 100";
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    errors.value = {};
    try {
        const formData = { ...form.value };

        // Validate homeroom_teacher_id is a valid UUID or null
        if (
            formData.homeroom_teacher_id &&
            typeof formData.homeroom_teacher_id === "string" &&
            formData.homeroom_teacher_id.length !== 36
        ) {
            console.error(
                "Invalid homeroom_teacher_id detected! Resetting to null.",
            );
            formData.homeroom_teacher_id = null;
        }

        if (props.isEditing && props.classSchooll) {
            await classSchoollStore.updateClass(
                props.classSchooll.id,
                formData,
            );
        } else {
            await classSchoollStore.createClass(formData);
        }
        resetForm();
        emit("saved");
    } catch (error: any) {
        if (error.response?.status === 422) {
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                errors.value[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
            return;
        }
        console.error("Save error:", error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadHomeroomTeachers();
    loadMajors();
});
</script>
